/**
 * C6-B — unificação de propostas, do lado do cliente.
 *
 * ── Por que existe `mensagemDaRecusa`
 *
 * O servidor recusa por REGRA com `422 {erro, mensagem}`, e a `mensagem` é uma
 * frase pronta em português ("Este card tem 2 dias. A unificação exige 7 dias
 * desde a criação."). Mas o `ApiError` monta o `.message` a partir de `error` —
 * com "r" no meio —, então quem ler só `err.message` recebe "Erro 422" e a
 * frase útil fica perdida dentro de `response`.
 *
 * Este helper é o único lugar que sabe disso. Sem ele, cada tela iria
 * redescobrir o formato, e a que esquecesse mostraria um código no lugar da
 * explicação — que é exatamente o "formulário surdo" que esta casa não aceita.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError, api, hasToken } from '@/lib/api';

export interface RevisaoDoGrupo {
  id: string;
  revisao: number;
  vigente: boolean;
  titulo: string;
  criadoEmIso: string;
}

export interface OrigemDoGrupo {
  cardId: string;
  numeroHumano: number;
  titulo: string;
  servicoNome: string | null;
  /** Preenchido quando a proposta saiu num desfazer parcial. */
  saiuEm: string | null;
  revisoes: RevisaoDoGrupo[];
}

export interface GrupoUnificado {
  id: string;
  clienteId: string;
  desfeitoEm: string | null;
  casca: { id: string; numeroHumano: number; titulo: string; revisoes: RevisaoDoGrupo[] } | null;
  origens: OrigemDoGrupo[];
}

const K = {
  grupo: (id: string) => ['v1', 'crm', 'unificacoes', id] as const,
  porCard: (cardId: string) => ['v1', 'crm', 'unificacoes', 'por-card', cardId] as const,
  cooldown: ['v1', 'crm', 'unificacoes', 'cooldown'] as const,
};

/**
 * A frase que o servidor mandou, ou `null` quando não houve uma.
 *
 * Devolver `null` em vez de um texto genérico é de propósito: quem chama decide
 * o que dizer quando não há frase, e ninguém acaba mostrando "Erro desconhecido"
 * achando que veio do servidor.
 */
export function mensagemDaRecusa(erro: unknown): string | null {
  if (!(erro instanceof ApiError)) return null;
  const corpo = erro.response as { mensagem?: unknown } | undefined;
  return typeof corpo?.mensagem === 'string' ? corpo.mensagem : null;
}

export function useGrupoUnificado(unificacaoId: string | null) {
  return useQuery({
    queryKey: K.grupo(unificacaoId ?? ''),
    queryFn: () => api.get<GrupoUnificado>(`/api/v1/crm/unificacoes/${unificacaoId}`),
    enabled: hasToken() && !!unificacaoId,
  });
}

/**
 * O grupo a que um card pertence, ou `null` quando ele nao esta em nenhum.
 *
 * O servidor devolve 200 com `unificacaoId: null` — nao mais 404. O `catch`
 * que traduzia o 404 saiu junto: ele fazia o cliente parecer tolerante a um
 * erro que nunca deveria ter existido.
 *
 * `staleTime` de 30s porque DUAS telas perguntam o mesmo (o cabecalho do card e
 * a aba Orcamento). Sem ele, cada montagem refazia a chamada.
 */
export function useUnificacaoDoCard(cardId: string | null) {
  return useQuery({
    queryKey: K.porCard(cardId ?? ''),
    queryFn: () =>
      api.get<{ unificacaoId: string | null }>(`/api/v1/crm/unificacoes/por-card/${cardId}`),
    enabled: hasToken() && !!cardId,
    staleTime: 30_000,
  });
}

export interface CandidatoUnificacao {
  id: string;
  numeroHumano: number;
  titulo: string;
  responsavelId: string | null;
  servicoNome: string | null;
  idadeDias: number;
}

/**
 * Candidatos para unificar com um card. O servidor ja filtra por dono, por
 * cliente e por estado — a tela nao refiltra, senao a regra passa a viver em
 * dois lugares.
 *
 * O cooldown vem JUNTO, na mesma resposta: a tela precisa dele para dizer
 * quantos dias faltam em cada linha, e uma segunda chamada so para isso deixaria
 * a lista aparecer antes da regra que a explica.
 */
export function useCandidatosUnificacao(cardId: string | null, q: string) {
  return useQuery({
    queryKey: ['v1', 'crm', 'unificacoes', 'buscar', cardId ?? '', q],
    queryFn: () =>
      api.get<{ itens: CandidatoUnificacao[]; cooldownDias: number }>(
        `/api/v1/crm/unificacoes/buscar?cardId=${encodeURIComponent(cardId!)}&q=${encodeURIComponent(q)}`,
      ),
    enabled: hasToken() && !!cardId,
    // 4xx nao se resolve tentando de novo: proposta sem cliente continua sem
    // cliente. Sem isto, cada tecla digitada disparava uma rodada de retries e
    // o console enchia de 422 — foi o que o Matheus viu na tela.
    retry: (_falhas, erro) => !(erro instanceof ApiError && erro.status >= 400 && erro.status < 500),
  });
}

export function useCooldownUnificacao() {
  return useQuery({
    queryKey: K.cooldown,
    queryFn: () => api.get<{ dias: number }>('/api/v1/crm/unificacoes/config/cooldown'),
    enabled: hasToken(),
  });
}

export function useUnificarPropostas() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cardIds: string[]) =>
      api.post<{ unificacaoId: string; cascaId: string; numeroHumano: number; orcamentoId: string | null }>(
        '/api/v1/crm/unificacoes',
        { cardIds },
      ),
    onSuccess: () => {
      // O funil muda inteiro: os originais somem e a casca aparece.
      void qc.invalidateQueries({ queryKey: ['v1', 'crm'] });
    },
  });
}

export function useDesfazerUnificacao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ unificacaoId, cardIds }: { unificacaoId: string; cardIds?: string[] }) =>
      // `api.delete` recebe RequestOptions, e o corpo vai dentro dele — o
      // desfazer PARCIAL precisa dizer quais saem, e não cabe na URL sem virar
      // uma lista de ids em query string.
      api.delete<{ desfeitos: number; restantes: number }>(
        `/api/v1/crm/unificacoes/${unificacaoId}`,
        { body: { cardIds } },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['v1', 'crm'] });
    },
  });
}
